import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useIsMobile } from "../../hooks/useIsMobile";
import { X, Send, Check, Loader2, Trash2 } from "lucide-react";
import IagoSpinner from "../iago/IagoSpinner";
import IagoAvatar from "../iago/IagoAvatar";
import clsx from "clsx";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { useStore } from "../../stores/store";
import { fmtRelative } from "../../lib/utils";
import { confirmAsync } from "../../stores/dialogStore";
import type { ChatAction, ChatMessage } from "../../types";

const ACTION_LABELS: Record<string, string> = {
  move_card: "Mover card",
  set_mood: "Alterar mood",
  add_subtask: "Adicionar subtarefa",
  archive_done_older_than: "Arquivar concluídos antigos",
  bulk_move_by_tag: "Mover em massa por tag",
};

export function ChatPanel() {
  const open = useStore((s) => s.chatOpen);
  const toggleChat = useStore((s) => s.toggleChat);
  const messages = useStore((s) => s.chatMessages);
  const sending = useStore((s) => s.chatSending);
  const sendChat = useStore((s) => s.sendChat);
  const approveAction = useStore((s) => s.approveAction);
  const clearChat = useStore((s) => s.clearChat);
  const loadChat = useStore((s) => s.loadChat);

  const [input, setInput] = useState("");
  const [history, setHistory] = useState<string[]>([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [executing, setExecuting] = useState<{ mid: number; idx: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (open && messages.length === 0) {
      loadChat();
    }
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  async function doSend() {
    const v = input.trim();
    if (!v || sending) return;
    setHistory((h) => (h[0] === v ? h : [v, ...h].slice(0, 20)));
    setHistoryIdx(-1);
    setInput("");
    await sendChat(v);
  }

  async function doApprove(mid: number, idx: number) {
    setExecuting({ mid, idx });
    try {
      await approveAction(mid, idx);
    } catch (e: any) {
      await confirmAsync({
        title: "Erro ao executar ação",
        description: e.message,
        confirmLabel: "OK",
        cancelLabel: "",
      });
    } finally {
      setExecuting(null);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          key="chat"
          initial={isMobile ? { y: "100%", opacity: 0 } : { x: 420, opacity: 0 }}
          animate={isMobile ? { y: 0, opacity: 1 } : { x: 0, opacity: 1 }}
          exit={isMobile ? { y: "100%", opacity: 0 } : { x: 420, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 260 }}
          className="glass fixed z-30 flex flex-col border-white/5 bottom-0 left-0 right-0 h-[85vh] rounded-t-2xl border-t md:left-auto md:top-0 md:h-auto md:w-[400px] md:max-w-[92vw] md:rounded-t-none md:border-t-0 md:border-l"
        >
          <div className="flex items-center justify-between border-b border-white/5 px-4 py-3">
            <div className="flex items-center gap-2.5">
              <IagoAvatar size={32} state={sending ? "thinking" : "idle"} />
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-semibold text-cyan-100">IAGO</span>
                <span className="text-[10px] text-slate-500">seu par na gestão de tarefas</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={async () => {
                  const ok = await confirmAsync({
                    title: "Limpar histórico do chat?",
                    description: "Todas as mensagens serão apagadas. Esta ação não pode ser desfeita.",
                    confirmLabel: "Limpar",
                    danger: true,
                  });
                  if (ok) clearChat();
                }}
                className="rounded p-1 text-slate-400 hover:bg-white/5 hover:text-rose-300"
                title="Limpar histórico"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={toggleChat}
                className="rounded p-1 text-slate-400 hover:bg-white/5 hover:text-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto scrollbar-thin px-4 py-4">
            {messages.length === 0 && !sending && (
              <div className="rounded-lg border border-dashed border-white/10 p-4 text-center text-xs text-slate-400">
                tá quieto aqui. me chama quando precisar.
              </div>
            )}

            {messages.map((m) => (
              <Message
                key={m.id}
                msg={m}
                onApprove={doApprove}
                executing={executing}
              />
            ))}

            {sending && (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <IagoAvatar size={20} state="thinking" />
                pensando…
              </div>
            )}
          </div>

          <div className="border-t border-white/5 p-3">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                    e.preventDefault();
                    doSend();
                  } else if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    doSend();
                  } else if (e.key === "ArrowUp" && input === "" && history.length > 0) {
                    e.preventDefault();
                    const next = Math.min(historyIdx + 1, history.length - 1);
                    setHistoryIdx(next);
                    setInput(history[next]);
                  } else if (e.key === "ArrowDown" && historyIdx >= 0) {
                    e.preventDefault();
                    const next = historyIdx - 1;
                    setHistoryIdx(next);
                    setInput(next >= 0 ? history[next] : "");
                  }
                }}
                placeholder="pergunta ou diz o que tá te atravessando…"
                className="min-h-[40px] max-h-[120px] flex-1 resize-none rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-500/60 focus:outline-none"
                rows={1}
              />
              <button
                onClick={doSend}
                disabled={!input.trim() || sending}
                className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 text-white transition hover:from-cyan-400 hover:to-teal-400 disabled:opacity-40"
              >
                {sending ? <IagoSpinner size={18} /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

function Message({
  msg,
  onApprove,
  executing,
}: {
  msg: ChatMessage;
  onApprove: (mid: number, idx: number) => void;
  executing: { mid: number; idx: number } | null;
}) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-gradient-to-br from-cyan-500/25 to-sky-500/20 px-3.5 py-2 text-sm text-cyan-50 shadow-[0_4px_16px_rgba(14,165,233,0.15)] ring-1 ring-cyan-400/25">
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-2.5">
      <div className="mt-0.5 shrink-0">
        <IagoAvatar size={28} />
      </div>
      <div className="flex-1 space-y-2">
        {msg.content && (
          <div className="prose-invert max-w-none rounded-2xl rounded-tl-sm border border-white/[0.06] bg-white/[0.04] px-3.5 py-2 text-sm shadow-[0_4px_16px_rgba(0,0,0,0.2)]">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{msg.content}</ReactMarkdown>
          </div>
        )}
        {msg.actions.map((a, i) => (
          <ActionCard
            key={i}
            action={a}
            onApprove={() => onApprove(msg.id, i)}
            executing={executing?.mid === msg.id && executing?.idx === i}
          />
        ))}
        <div className="text-[10px] text-slate-500">{fmtRelative(msg.createdAt)}</div>
      </div>
    </div>
  );
}

function ActionCard({
  action,
  onApprove,
  executing,
}: {
  action: ChatAction;
  onApprove: () => void;
  executing: boolean;
}) {
  const label = ACTION_LABELS[action.kind] || action.kind;
  return (
    <div
      className={clsx(
        "rounded-lg border px-3 py-2.5 text-xs",
        action.executed
          ? "border-emerald-500/30 bg-emerald-500/5"
          : "border-cyan-500/30 bg-cyan-500/5",
      )}
    >
      <div className="mb-1 flex items-center justify-between">
        <span className="font-semibold uppercase tracking-wider text-[10px] text-cyan-300">{label}</span>
        {action.executed && (
          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300">
            <Check className="h-3 w-3" /> executada
          </span>
        )}
      </div>
      <p className="mb-2 text-slate-200">{action.preview || <em className="text-slate-500">sem preview</em>}</p>
      <pre className="mb-2 overflow-x-auto rounded bg-black/40 p-1.5 font-mono text-[10px] text-slate-300">
        {JSON.stringify(action.params, null, 2)}
      </pre>
      {!action.executed && (
        <div className="flex justify-end gap-1.5">
          <button
            onClick={onApprove}
            disabled={executing}
            className="flex items-center gap-1 rounded-md bg-gradient-to-br from-cyan-500 to-teal-500 px-2 py-1 text-[11px] font-medium text-white transition hover:from-cyan-400 hover:to-teal-400 disabled:opacity-40"
          >
            {executing ? <IagoSpinner size={14} /> : <Check className="h-3 w-3" />}
            Aprovar
          </button>
        </div>
      )}
    </div>
  );
}
